import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'en', flag: '🇬🇧' },
  { code: 'fr', flag: '🇫🇷' },
  { code: 'da', flag: '🇩🇰' },
  { code: 'de', flag: '🇩🇪' },
];

export default function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = languages.find((l) => l.code === i18n.language) ?? languages[0];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function changeLanguage(code: string) {
    i18n.changeLanguage(code);
    setOpen(false);
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer whitespace-nowrap"
        aria-label={t('languages.' + current.code)}
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span className="hidden sm:inline">{current.code.toUpperCase()}</span>
        <i className="ri-arrow-down-s-line text-foreground-500"></i>
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-44 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1">
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => changeLanguage(lang.code)}
              className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors cursor-pointer whitespace-nowrap ${
                lang.code === current.code
                  ? 'bg-primary-100 text-primary-800 font-medium'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              <span className="text-base leading-none">{lang.flag}</span>
              <span>{t('languages.' + lang.code)}</span>
              {lang.code === current.code && <i className="ri-check-line ml-auto"></i>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}